/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  shallowRef,
  triggerRef,
} from "vue";
import { LemmonGrid, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem, StockUnit } from "./types";
import { buildStockSchema } from "./stockSchema";
import { AddStockItemModal, type NewStockItem } from "./AddStockItemModal";
import { EditUnitsModal } from "./EditUnitsModal";
import { StockItemHistoryModal } from "./StockItemHistoryModal";
import { ShoppingListModal } from "./ShoppingListModal";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// The shop's stock items: on hand, par level, units and costs.
// See docs/plans/inventory_and_costing/03_data_model.md (#7).
export const InventoryStockPage = defineComponent({
  name: "InventoryStockPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const error = ref<string | null>(null);
    const data = shallowRef<StockItem[]>([]);
    const changedRowIds = ref<Set<number | string>>(new Set());
    const saving = ref(false);
    const showAdd = ref(false);
    const unitsItem = ref<StockItem | null>(null);
    const historyItem = ref<StockItem | null>(null);
    const belowParOnly = ref(false);
    const showShopping = ref(false);

    const isBelowPar = (item: StockItem) =>
      item.par_level != null && item.qty_on_hand < item.par_level;
    const belowParCount = computed(() => data.value.filter(isBelowPar).length);
    // Negative levels (not counted yet) don't count towards value.
    const stockValue = computed(() =>
      data.value.reduce(
        (sum, i) => sum + Math.max(i.qty_on_hand, 0) * i.avg_cost,
        0,
      ),
    );
    const rows = computed(() =>
      belowParOnly.value ? data.value.filter(isBelowPar) : data.value,
    );

    const hasChanges = computed(() => changedRowIds.value.size > 0);
    const schema = computed(() =>
      buildStockSchema(
        t,
        (item) => (unitsItem.value = item),
        (item) => (historyItem.value = item),
      ),
    );

    const load = async () => {
      data.value = (await trpc.stock.list.query()) as StockItem[];
    };

    onMounted(async () => {
      try {
        await load();
      } catch {
        error.value = t("inventory.stock.load_failed");
      } finally {
        loading.value = false;
      }
    });

    const errorMessage = (e: unknown, fallback: string) =>
      (e as { data?: { code?: string }; message?: string })?.data?.code ===
      "CONFLICT"
        ? ((e as { message?: string }).message ?? fallback)
        : fallback;

    const handleCellChange = (
      rowId: string | number,
      field: string,
      value: any,
    ) => {
      const row = data.value.find((r) => r.id === rowId);
      if (!row) return;
      (row as any)[field] = value;
      changedRowIds.value = new Set([...changedRowIds.value, rowId]);
      triggerRef(data);
    };

    const handleBulkChange = (
      rowIds: Set<string | number>,
      field: string,
      value: any,
    ) => {
      for (const id of rowIds) handleCellChange(id, field, value);
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const changed = data.value.filter((r) => changedRowIds.value.has(r.id));
        await Promise.all(
          changed.map((item) =>
            trpc.stock.update.mutate({
              id: item.id,
              name: item.name,
              stock_category: item.stock_category || null,
              par_level:
                item.par_level == null || (item.par_level as unknown) === ""
                  ? null
                  : Number(item.par_level),
              active: Boolean(item.active),
            }),
          ),
        );
        changedRowIds.value = new Set();
        await load();
        toast.showToast(t("inventory.stock.saved"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.stock.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const handleDiscard = async () => {
      changedRowIds.value = new Set();
      await load();
    };

    const handleDeleteRows = async (rowIds: Set<string | number>) => {
      if (rowIds.size === 0) return;
      if (
        !confirm(
          t("inventory.stock.delete_confirm").replace(
            "{count}",
            String(rowIds.size),
          ),
        )
      ) {
        return;
      }
      try {
        for (const id of rowIds)
          await trpc.stock.delete.mutate({ id: id as number });
        toast.showToast(t("inventory.stock.deleted"), { type: "success" });
      } catch (e) {
        toast.showToast(errorMessage(e, t("inventory.stock.delete_failed")), {
          type: "error",
        });
      }
      await load();
    };

    const handleAdd = async (item: NewStockItem) => {
      saving.value = true;
      try {
        await trpc.stock.create.mutate(item);
        showAdd.value = false;
        await load();
        toast.showToast(t("inventory.stock.created"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.stock.create_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const handleSaveUnits = async (units: StockUnit[]) => {
      if (!unitsItem.value) return;
      saving.value = true;
      try {
        await trpc.stock.setUnits.mutate({ id: unitsItem.value.id, units });
        unitsItem.value = null;
        await load();
        toast.showToast(t("inventory.stock.saved"), { type: "success" });
      } catch (e) {
        toast.showToast(errorMessage(e, t("inventory.stock.save_failed")), {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    return () => {
      if (loading.value) {
        return (
          <div class="flex h-full w-full items-center justify-center">
            <Loading />
          </div>
        );
      }
      if (error.value) {
        return (
          <div class="flex h-full w-full items-center justify-center text-error">
            {error.value}
          </div>
        );
      }

      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          <SubpageHeader
            title={t("inventory.nav.stock")}
            subtitle={t("inventory.stock.subtitle")}
            v-slots={{
              badges: () => (
                <>
                  <span class="rounded-md border border-base-300 bg-base-100 px-2 py-0.5 text-xs font-medium text-base-content/60">
                    {data.value.length}
                  </span>
                  {hasChanges.value && (
                    <span class="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {t("inventory.stock.changed").replace(
                        "{count}",
                        String(changedRowIds.value.size),
                      )}
                    </span>
                  )}
                </>
              ),
              default: () => (
                <>
                  <div class="hidden items-center gap-4 text-sm md:flex">
                    <div class="text-right">
                      <div class="text-xs text-base-content/60">
                        {t("inventory.stock.value")}
                      </div>
                      <div class="font-semibold">
                        {stockValue.value.toFixed(2)}
                      </div>
                    </div>
                    <button
                      class={[
                        "btn btn-sm",
                        belowParOnly.value ? "btn-warning" : "btn-ghost",
                      ].join(" ")}
                      onClick={() => (belowParOnly.value = !belowParOnly.value)}
                    >
                      <span class="material-symbols-outlined text-base">
                        trending_down
                      </span>
                      {t("inventory.stock.below_par").replace(
                        "{count}",
                        String(belowParCount.value),
                      )}
                    </button>
                    <button
                      class="btn btn-ghost btn-sm"
                      onClick={() => (showShopping.value = true)}
                    >
                      <span class="material-symbols-outlined text-base">
                        shopping_cart
                      </span>
                      {t("inventory.shopping.title")}
                    </button>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      class="btn btn-neutral btn-sm"
                      onClick={() => (showAdd.value = true)}
                    >
                      <span class="material-symbols-outlined text-base">
                        add
                      </span>
                      {t("inventory.stock.add_item")}
                    </button>
                    {hasChanges.value && (
                      <>
                        <button
                          class="btn btn-ghost btn-sm text-base-content/70"
                          onClick={handleDiscard}
                        >
                          <span class="material-symbols-outlined text-base">
                            undo
                          </span>
                          {t("inventory.stock.discard")}
                        </button>
                        <button
                          class="btn btn-primary btn-sm"
                          disabled={saving.value}
                          onClick={handleSave}
                        >
                          <span class="material-symbols-outlined text-base">
                            save
                          </span>
                          {t("general.words.save")}
                        </button>
                      </>
                    )}
                  </div>
                </>
              ),
            }}
          />

          <div class="flex-1 overflow-hidden p-3">
            {data.value.length === 0 ? (
              <div class="flex h-full flex-col items-center justify-center gap-2 text-center">
                <span class="material-symbols-outlined text-5xl text-base-content/30">
                  inventory
                </span>
                <p class="text-sm text-base-content/60">
                  {t("inventory.stock.empty")}
                </p>
              </div>
            ) : (
              <LemmonGrid
                data={rows.value}
                schema={schema.value}
                changedRowIds={changedRowIds.value}
                onCellChange={handleCellChange}
                onBulkChange={handleBulkChange}
                onDeleteRows={handleDeleteRows}
              />
            )}
          </div>

          {showAdd.value && (
            <AddStockItemModal
              saving={saving.value}
              onClose={() => (showAdd.value = false)}
              onAdd={handleAdd}
            />
          )}
          {showShopping.value && (
            <ShoppingListModal onClose={() => (showShopping.value = false)} />
          )}
          {historyItem.value && (
            <StockItemHistoryModal
              item={historyItem.value}
              onClose={() => (historyItem.value = null)}
            />
          )}
          {unitsItem.value && (
            <EditUnitsModal
              item={unitsItem.value}
              saving={saving.value}
              onClose={() => (unitsItem.value = null)}
              onSave={handleSaveUnits}
            />
          )}
        </div>
      );
    };
  },
});
